import type { KeyboardEvent } from 'react';

import { useQueryBuilderContext } from '../context';
import { findAdjacentTextNavCell, focusForTextEditing, focusQueryEdge, getTextNavCells } from '../utils';

import type { GridSelectionState } from './useGridSelection';
import { useKeyboardSelectionContext } from './useKeyboardSelection';

/**
 * Provides keyboard navigation props for a single token row inside the FilterBar grid.
 *
 * - Notifies the grid's GridSelectionState of the focused token so virtual focus tracks it.
 *
 * @param tokenKey - The stable key identifying this token in the grid.
 * @param state    - The GridSelectionState from FilterBar; may be null when used standalone.
 */
export function useQueryBuilderGridItem(
  tokenKey: string,
  state: GridSelectionState | null,
  ariaLabel?: string,
): {
  containerProps: React.HTMLAttributes<HTMLElement> & { 'data-key': string; role?: 'row' };
  cellProps: { role?: 'gridcell' };
} {
  const { wrapperRef, setActivePopover } = useQueryBuilderContext();
  const selCtx = useKeyboardSelectionContext();

  /**
   * Returns the search root for focusable-element traversal.
   * Priority:
   *  1. Nearest [role="grid"] ancestor — set by the default FilterBar.
   *  2. Nearest [data-qb-nav-root] ancestor — set by the inline FilterBar
   *     (InlineFilterMenu), which has no grid. Scoping to it keeps arrow
   *     navigation within the lozenges + Filter trigger and out of sibling
   *     controls (e.g. an adjacent query input).
   *  3. wrapperRef — set by <QueryBuilder> (static-children pattern).
   */
  function getRoot(fromContainer: HTMLElement): HTMLElement | null {
    return fromContainer.closest<HTMLElement>('[role="grid"], [data-qb-nav-root]') ?? wrapperRef.current;
  }

  function focusNextToken(currentContainer: HTMLElement): boolean {
    const root = getRoot(currentContainer);
    if (!root) {
      return false;
    }
    const next = findAdjacentTextNavCell(root, currentContainer, 'right');
    if (next) {
      focusForTextEditing(next, { caret: 'start', setActivePopover });
      return true;
    }
    return false;
  }

  function focusPreviousToken(currentContainer: HTMLElement): boolean {
    const root = getRoot(currentContainer);
    if (!root) {
      return false;
    }
    const prev = findAdjacentTextNavCell(root, currentContainer, 'left');
    if (prev) {
      focusForTextEditing(prev, { caret: 'end', setActivePopover });
      return true;
    }
    return false;
  }

  // Only apply grid ARIA roles and keyboard handlers when inside a grid context
  // (state is null when LozengeFilter is used standalone without a tokens-based FilterBar).
  const role = state ? ('row' as const) : undefined;

  const containerProps = {
    ...(role ? { role, ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}) } : {}),
    ...(state ? { 'aria-selected': state.isSelected(tokenKey) } : {}),
    'data-key': tokenKey,
    'data-token-container': '',

    onFocus() {
      state?.setFocusedKey(tokenKey);
    },

    onKeyDownCapture(e: KeyboardEvent<HTMLElement>) {
      // Shift+Arrow initiates token selection, but only when the cursor is already
      // at the boundary of an input (position 0 for left, value.length for right).
      // When mid-input, let the browser handle text selection normally.
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && state && selCtx) {
        const target = e.target as HTMLElement;
        if (target instanceof HTMLInputElement) {
          const atBoundary =
            e.key === 'ArrowLeft'
              ? target.selectionStart === 0 && target.selectionEnd === 0
              : target.selectionStart === target.value.length && target.selectionEnd === target.value.length;
          if (!atBoundary) {
            return;
          }
        }
        e.preventDefault();
        selCtx.selectInDirection(e.key === 'ArrowLeft' ? 'left' : 'right', tokenKey);
        return;
      }

      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }

      if (state && (e.metaKey || e.ctrlKey || e.altKey)) {
        const target = e.target as HTMLElement;
        if (target instanceof HTMLInputElement) {
          const atBoundary =
            e.key === 'ArrowLeft'
              ? target.selectionStart === 0 && target.selectionEnd === 0
              : target.selectionStart === target.value.length && target.selectionEnd === target.value.length;
          if (!atBoundary) {
            return;
          }
        }
        const containerEl = e.currentTarget as HTMLElement;
        const root = getRoot(containerEl);
        const dir = e.key === 'ArrowLeft' ? 'left' : 'right';
        if (!root) {
          return;
        }
        if (e.metaKey || e.ctrlKey) {
          if (focusQueryEdge(root, dir, setActivePopover)) {
            e.preventDefault();
          }
          return;
        }
        const fromEl = target instanceof HTMLElement ? target : containerEl;
        const adjacent = findAdjacentTextNavCell(root, fromEl, dir);
        if (adjacent) {
          e.preventDefault();
          focusForTextEditing(adjacent, {
            caret: dir === 'left' ? 'end' : 'start',
            setActivePopover,
            word: dir,
          });
        }
        return;
      }

      const container = e.currentTarget as HTMLElement;
      const target = e.target as HTMLElement;
      const cells = getTextNavCells(container);
      const idx = cells.indexOf(target);

      if (e.key === 'ArrowRight') {
        // If there's a next cell within this token, move to it.
        if (idx >= 0 && idx < cells.length - 1) {
          if (target instanceof HTMLInputElement) {
            if (target.selectionStart !== target.value.length) {
              return;
            }
          } else if (isLozengeTextPart(target)) {
            e.preventDefault();
            focusForTextEditing(target, { caret: 'start', setActivePopover });
            return;
          }
          e.preventDefault();
          focusForTextEditing(cells[idx + 1], { caret: 'start', setActivePopover });
          return;
        }
        if (target instanceof HTMLInputElement) {
          if (target.selectionStart !== target.value.length) {
            return;
          }
        } else if (isLozengeTextPart(target)) {
          e.preventDefault();
          focusForTextEditing(target, { caret: 'start', setActivePopover });
          return;
        }
        if (focusNextToken(container)) {
          e.preventDefault();
        }
        return;
      }

      // ArrowLeft
      if (idx > 0) {
        if (target instanceof HTMLInputElement) {
          if (target.selectionStart !== 0) {
            return;
          }
        } else if (isLozengeTextPart(target)) {
          e.preventDefault();
          focusForTextEditing(target, { caret: 'end', setActivePopover });
          return;
        }
        e.preventDefault();
        focusForTextEditing(cells[idx - 1], { caret: 'end', setActivePopover });
        return;
      }
      if (target instanceof HTMLInputElement) {
        if (target.selectionStart !== 0) {
          return;
        }
      } else if (isLozengeTextPart(target)) {
        e.preventDefault();
        focusForTextEditing(target, { caret: 'end', setActivePopover });
        return;
      }
      if (focusPreviousToken(container)) {
        e.preventDefault();
      }
    },
  };

  const cellProps = state ? { role: 'gridcell' as const } : {};

  return { containerProps, cellProps };
}

function isLozengeTextPart(el: HTMLElement): boolean {
  const part = el.getAttribute('data-part');
  return part === 'key' || part === 'op' || part === 'value' || (part === 'operator' && el.tagName === 'BUTTON');
}
